import { queryOptions, useQuery } from "@tanstack/react-query"
import axios from "axios"
import { DataResponse, ShopifyProductsResponseDataSchema } from "schema"
import z from "zod"

type ShopifyProducts = z.infer<typeof ShopifyProductsResponseDataSchema>
type Response = DataResponse<ShopifyProducts>

const VARIANT_OPTIONS_PRODUCTS_LIMIT = 50

function isDefaultTitleOption(option: ShopifyProducts[number]["options"][number]) {
  return (
    option.name === "Title" && option.values.length === 1 && option.values[0] === "Default Title"
  )
}

function getVariantOptions(products: ShopifyProducts) {
  const options = new Map<string, string>()

  for (const product of products) {
    for (const option of product.options) {
      if (isDefaultTitleOption(option)) continue

      const optionName = option.name.trim()
      if (!optionName) continue

      const key = optionName.toLowerCase()
      if (!options.has(key)) {
        options.set(key, optionName)
      }
    }
  }

  return [...options.values()]
}

const shopifyVariantOptionsQuery = () =>
  queryOptions({
    queryKey: ["shopify", "variant-options", { limit: VARIANT_OPTIONS_PRODUCTS_LIMIT }],
    queryFn: async () => {
      const response = await axios.get<Response>("/integrations/shopify/products", {
        params: { query: "", limit: VARIANT_OPTIONS_PRODUCTS_LIMIT },
      })

      return getVariantOptions(response.data.data)
    },
    staleTime: 5 * 60 * 1000,
  })

export function useShopifyVariantOptionsQuery(enabled: boolean) {
  return useQuery({ ...shopifyVariantOptionsQuery(), enabled })
}
